import { FirebaseError } from "firebase/app";
import {
  signInWithEmailLink as _signInWithEmailLink,
  getAdditionalUserInfo
} from "firebase/auth";

import { Dispatch, GetState } from "common/types";
import { UserAccountType } from "common/types/user";

import {
  getFirebaseAuth,
  getSignInViaEmailLinkLocalStorageData,
  removeSignInViaEmailLinkLocalStorageFields,
  setSignInViaEmailLinkLocalStorageData,
  SignInViaEmailPreconnectedParentMeta
} from "services/firebase/auth";
import {
  logAnalyticsAuthError,
  logAnalyticsAuthStart
} from "services/firebase/auth/analytics/events";
import { getUserLinkedProviders } from "services/firebase/auth/ssoProvider";

import {
  getRegion,
  signInError,
  signInStart
} from "seneca-common/features/user/state";

import { PreconnectedParentSignInData } from "features/passwordless-sign-in/parent/types";

import { proceedUserSignIn, proceedUserSignUp } from "../sso/utils";
import errorTranslator from "./errorTranslator";

type Props = {
  email: string;
  windowLocationHref: string;
  accountType?: UserAccountType;
  onSignUpSuccess?: () => void;
  onSignInSuccess?: () => void;
  onError: (message: string) => void;
};

export default function signInWithEmailLink({
  email,
  windowLocationHref,
  accountType,
  onSignUpSuccess,
  onSignInSuccess,
  onError
}: Props) {
  return async function (dispatch: Dispatch, getState: GetState) {
    const {
      email: _,
      signInStarted,
      ...metadata
    } = getSignInViaEmailLinkLocalStorageData<PreconnectedParentSignInData>() ??
    {};

    if (signInStarted) return;

    logAnalyticsAuthStart({ providerId: "emailLink" });

    if (!email) throw new Error("Email not defined ");

    const region = getRegion(getState());
    dispatch(signInStart());

    setSignInViaEmailLinkLocalStorageData<SignInViaEmailPreconnectedParentMeta>(
      {
        email,
        ...metadata,
        signInStarted: true
      }
    );

    try {
      const auth = getFirebaseAuth();

      const authInfo = await _signInWithEmailLink(
        auth,
        email,
        windowLocationHref
      );

      const additionalUserInfo = getAdditionalUserInfo(authInfo);

      const shouldSignUp = !!additionalUserInfo?.isNewUser;

      if (shouldSignUp) {
        const userDetails = {
          email,
          providerData: getUserLinkedProviders(),
          ...(accountType && {
            type: accountType
          })
        };
        await dispatch(
          proceedUserSignUp({
            userInfoToSave: userDetails,
            onSignUpSuccess,
            region
          })
        );
      } else {
        await dispatch(
          proceedUserSignIn({
            region,
            userId: authInfo.user.uid,
            onSignInSuccess
          })
        );
      }
    } catch (err) {
      dispatch(signInError());

      removeSignInViaEmailLinkLocalStorageFields("signInStarted");

      logAnalyticsAuthError({
        error: err instanceof FirebaseError ? err.code : (err as Error)?.message
      });

      onError(errorTranslator(err));
    }
  };
}
